import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Container, Spinner, Table
} from 'react-bootstrap';
import { FaFileExcel, FaChartBar, FaSyncAlt } from 'react-icons/fa';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';
import { motion, AnimatePresence } from 'framer-motion';
import * as XLSX from 'xlsx';

ChartJS.register(
  BarElement, CategoryScale, LinearScale,
  PointElement, Title, Tooltip, Legend, Filler
);

const COLORS = [
  'rgba(54, 162, 235, 0.7)',
  'rgba(255, 99, 132, 0.7)',
  'rgba(75, 192, 192, 0.7)',
  'rgba(255, 206, 86, 0.7)',
  'rgba(153, 102, 255, 0.7)',
  'rgba(255, 159, 64, 0.7)'
];

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const AnnualReportService = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [annualReportData, setAnnualReportData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchAnnualReportData();
  }, []);

  const fetchAnnualReportData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const userData = await userResponse.json();

      const annualReportResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/${userData.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!annualReportResponse.ok) {
        if (annualReportResponse.status === 404) {
          setAnnualReportData(null);
          return;
        }
        throw new Error('Failed to fetch annual report data');
      }

      const data = await annualReportResponse.json();
      setAnnualReportData(data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const exportToExcel = () => {
    if (!annualReportData) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      annualReportData.rows.filter(row => !row.include)
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Annual Report Data");
    XLSX.writeFile(workbook, `${annualReportData.title || 'annual_report_data'}.xlsx`);
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        position: 'top',
        labels: {
          font: {
            family: 'Arial',
            size: 12,
            weight: 'bold'
          },
          padding: 20,
          usePointStyle: true,
        }
      },
      title: {
        display: true,
        text: annualReportData?.title || 'Annual Report Data',
        font: { 
          size: 18,
          family: 'Arial',
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 12
        },
        padding: 10,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            return ` $${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart',
    },
    elements: {
      bar: {
        borderRadius: 6,
        borderSkipped: 'bottom',
      }
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false
        },
        ticks: {
          font: {
            weight: 'bold'
          }
        }
      },
      y: {
        beginAtZero: false,
        grid: {
          color: 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          callback: value => '$' + value.toLocaleString(),
          font: {
            weight: 'bold'
          }
        },
      },
    },
    layout: {
      padding: {
        left: 20,
        right: 20,
        top: 20,
        bottom: 20
      }
    }
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="d-flex justify-content-center align-items-center vh-100"
      >
        <div className="text-center">
          <Spinner animation="grow" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Loading your annual report data...</p>
        </div>
      </motion.div>
    );
  }

  return (
    <Container fluid className="py-4 px-3 px-md-4">
      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mb-4"
          >
            <Alert 
              variant="danger" 
              className="d-flex flex-column flex-md-row justify-content-between align-items-center shadow-sm"
            >
              <div className="mb-2 mb-md-0">
                <strong>Error:</strong> {error}
              </div>
              <Button 
                variant="outline-danger" 
                size="sm" 
                onClick={fetchAnnualReportData}
                disabled={isRefreshing}
              >
                {isRefreshing ? 'Refreshing...' : 'Try Again'}
              </Button>
            </Alert>
          </motion.div>
        )}

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
          className="d-flex flex-column gap-4"
        >
          {annualReportData ? (
            <>
              {/* Additional Data Table */}
              <motion.div variants={fadeIn}>
                <Card className="shadow-lg border-0">
                  <Card.Header className="bg-gradient-primary text-white d-flex flex-column flex-md-row justify-content-between align-items-center">
                    <div className="d-flex align-items-center mb-2 mb-md-0">
                      <FaFileExcel className="me-2" size={20} />
                      <h5 className="mb-0">{annualReportData.additionalTitle || 'Annual Report Records'}</h5>
                    </div>
                    <div className="d-flex">
                      <Button 
                        variant="light" 
                        size="sm" 
                        className="me-2"
                        onClick={exportToExcel}
                      >
                        <span className="d-none d-md-inline">Export Excel</span>
                        <span className="d-inline d-md-none"><FaFileExcel /></span>
                      </Button>
                      <Button 
                        variant="light" 
                        size="sm" 
                        onClick={fetchAnnualReportData}
                        disabled={isRefreshing}
                      >
                        <FaSyncAlt className={isRefreshing ? "spin" : ""} />
                      </Button>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="bg-light">
                          <tr>
                            {annualReportData.headers.map((header, index) => (
                              <th key={index} className="py-3 px-2 px-md-4 text-uppercase text-muted">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>  
                        <tbody>
                          {annualReportData.rows.filter(row => !row.include).map((row, rowIndex) => (
                            <motion.tr 
                              key={rowIndex}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: rowIndex * 0.05 }}
                              className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-light'}
                            >
                              {annualReportData.headers.map((header, colIndex) => (
                                <td key={colIndex} className="py-3 px-2 px-md-4">
                                  {typeof row[header] === 'number' ? 
                                    `$${row[header].toLocaleString()}` : 
                                    row[header]}
                                </td>
                              ))}
                            </motion.tr>
                          ))}
                          {!annualReportData.rows.filter(row => !row.include).length && (
                            <tr>
                              <td colSpan={annualReportData.headers.length} className="text-center text-muted py-5">
                                No additional data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </motion.div>

              {/* Chart Section */}
              {annualReportData.chartData && (
                <motion.div variants={fadeIn}>
                  <Card className="shadow-lg border-0">
                    <Card.Header className="bg-gradient-primary text-white d-flex flex-column flex-md-row justify-content-between align-items-center">
                      <div className="d-flex align-items-center mb-2 mb-md-0">
                        <FaChartBar className="me-2" size={20} />
                        <h5 className="mb-0">{annualReportData.title || 'Annual Report Overview'}</h5>
                      </div>
                      <div>
                        <Button 
                          variant="light" 
                          size="sm" 
                          onClick={fetchAnnualReportData}
                          disabled={isRefreshing}
                        >
                          <FaSyncAlt className={isRefreshing ? "spin" : ""} />
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body style={{ minHeight: '300px', height: '50vh' }} className="p-2 p-md-4">
                      <Bar 
                        data={annualReportData.chartData} 
                        options={chartOptions} 
                        className="w-100 h-100"
                      />
                    </Card.Body>
                  </Card>
                </motion.div>
              )}
            </>
          ) : (
            <motion.div variants={fadeIn}>
              <Card className="text-center py-5 border-0 shadow-sm">
                <Card.Body>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.05, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <FaFileExcel size={64} className="text-primary mb-3" />
                  </motion.div>
                  <h4 className="text-dark mb-3">No annual report data available</h4>
                  <p className="text-muted mb-4">
                    Please contact your administrator to set up your annual report records
                  </p>
                  <Button 
                    variant="primary" 
                    onClick={fetchAnnualReportData}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? 'Checking...' : 'Check Again'}
                  </Button>
                </Card.Body>
              </Card>
            </motion.div>
          )}
        </motion.div>
      </AnimatePresence>

      <style jsx global>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%);
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .table-responsive {
          border-radius: 0.5rem;
          overflow: hidden;
        }
        thead {
          backdrop-filter: blur(10px);
        }
        
        /* Responsive adjustments */
        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .chart-container {
            min-height: 300px !important;
          }
        }
        
        @media (max-width: 576px) {
          .card-header h5 {
            font-size: 1.1rem;
          }
          .table th, .table td {
            padding: 0.5rem;
            font-size: 0.85rem;
          }
        }
      `}</style>
    </Container>
  );
};

export default AnnualReportService;
